/**
 * React-query hooks for the GO Lab snapshot endpoints (B6).
 *
 * Same conventions as `useAnnotation.ts`: hierarchical `*Keys` factory
 * (namespaced under `['golab', 'snapshots']`), 30s `staleTime`, 404 retry
 * skip, no hook-level toasts. Mutation invalidation is wider for
 * `useRollbackSnapshot` — rollback flips snapshot statuses AND reverts
 * records AND flips `is_current` on annotations and resolutions, so the
 * entire annotation cache also needs busting.
 */

import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQueryClient,
} from '@tanstack/react-query';

import { skipRetryOn404 } from '../../../../../services/types/errors';
import { snapshotService } from '../services/snapshotService';
import type {
  CreateSnapshotPayload,
  ListPaginationParams,
  RollbackResult,
  Snapshot,
} from '../types';
import { annotationKeys } from './useAnnotation';


/**
 * Stable query-key factory under `['golab', 'snapshots']`. Snapshots are
 * org-wide (no per-record / per-collection sub-keys) so the tree is shallow.
 */
export const snapshotKeys = {
  all: ['golab', 'snapshots'] as const,
  list: (params: ListPaginationParams) =>
    [...snapshotKeys.all, 'list', params] as const,
};


/**
 * Observe the first `pageCount` snapshot pages at once — one query per
 * offset. Keeping every revealed page an *active* observer means a mutation
 * that invalidates the snapshot key tree (`useCreateSnapshot` /
 * `useRollbackSnapshot`) refetches all of them, not just the page currently
 * in view — so a save/rollback while scrolled past the first page still
 * surfaces the new active snapshot and status changes at the top. Returns
 * the pages newest-first plus aggregate loading / error / fetching flags.
 */
export const useSnapshotPages = (pageCount: number, pageSize: number) => {
  const results = useQueries({
    queries: Array.from({ length: Math.max(pageCount, 1) }, (_unused, page) => {
      const params: ListPaginationParams = {
        limit: pageSize,
        offset: page * pageSize,
      };
      return {
        queryKey: snapshotKeys.list(params),
        queryFn: () => snapshotService.listSnapshots(params),
        staleTime: 30 * 1000,
        placeholderData: keepPreviousData,
        retry: skipRetryOn404,
      };
    }),
  });
  return {
    pages: results.map((r) => r.data),
    total: results[0]?.data?.total ?? 0,
    // Skeleton only on the first page's initial load; later pages show their
    // progress via `isFetching` (the "Load more" button) rather than
    // collapsing the whole list back to the loading state.
    isLoading: results[0]?.isLoading ?? false,
    // A first-page failure is the canonical "couldn't load snapshots"; a
    // failed "Load more" leaves the already-loaded list in place.
    isError: results[0]?.isError ?? false,
    isFetching: results.some((r) => r.isFetching),
    refetch: () => {
      results.forEach((r) => {
        void r.refetch();
      });
    },
  };
};


/**
 * Capture a new snapshot. On success invalidates every snapshot key — the
 * new snapshot becomes active and the previously-active one is archived,
 * so any cached list is stale.
 */
export const useCreateSnapshot = () => {
  const qc = useQueryClient();
  return useMutation<Snapshot, Error, CreateSnapshotPayload>({
    mutationFn: snapshotService.createSnapshot,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: snapshotKeys.all });
    },
  });
};


// Datasets-tab caches (collection list, channel datasets, overview,
// coverage, quality) are derived from record statuses and `is_current`
// annotations — exactly what a rollback flips. Inlined as a literal
// tuple (same rationale as `useAnnotation.ts`) to avoid a cross-area
// import: `datasets/hooks/useDatasets.ts` already depends on
// `annotationKeys`; importing `datasetKeys` here would create a cycle.
// Mirrors `datasetKeys.all` in `../../datasets/hooks/useDatasets.ts:55`.
const _DATASET_KEY_ROOT = ['golab', 'datasets'] as const;


/**
 * Restore a prior snapshot. On success the snapshot statuses, the records'
 * statuses, and the `is_current` flags on annotations + resolutions have
 * all moved — bust the snapshot tree, the entire annotation tree, AND
 * the datasets tree (channel stats / overview / coverage / quality all
 * derive from the same records the rollback just reverted).
 */
export const useRollbackSnapshot = () => {
  const qc = useQueryClient();
  return useMutation<RollbackResult, Error, string>({
    mutationFn: snapshotService.rollbackSnapshot,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: snapshotKeys.all });
      void qc.invalidateQueries({ queryKey: annotationKeys.all });
      void qc.invalidateQueries({ queryKey: _DATASET_KEY_ROOT });
    },
  });
};
